import React, { memo, useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Modal, ScrollView, Platform } from "react-native";
import { Text, TextInput, Button, IconButton } from "react-native-paper";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { BlurView } from "expo-blur";
import DateTimePicker from "@react-native-community/datetimepicker";
import Spinner from "../../../components/shared/Spinner";
import {
  getPortalOrderByOrderNo,
  updateDeliveryPortalStatusAddress,
} from "../../../services/deliveryService";
import { DatePickerInput } from "react-native-paper-dates";
import moment from "moment";

// Types
interface OrderItem {
  ItemCode: string;
  Dscription: string;
  RequiredDate: Date | null;
  itemName?: string;
  existsInApi?: boolean;
}

interface SelectedItem {
  DocNum: string;
  CustomerPortalOrderNo: string;
  OrderStatus: string;
  Warehouse: string;
  DeliveryAddress: string;
  items: OrderItem[];
}

interface FormData {
  DocNum: string;
  CustomerPortalOrderNo: string;
  OrderStatus: string;
  Warehouse: string;
  DeliveryAddress: string;
  ShipmentStatus: string;
  items: OrderItem[];
}

interface DeliveryStatusConfirmModalProps {
  selectedItem: SelectedItem | null;
  modalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
  onSuccess?: () => void;
}

// Status validation helper
const getStatusValidation = (shipmentStatus: string, orderStatus: string) => {
  const normalizedShipment = shipmentStatus?.toLowerCase() || "";
  const normalizedOrder = orderStatus?.toLowerCase() || "";

  if (normalizedShipment === "cancelled") {
    return {
      isValid: false,
      message: "Portal Order is already cancelled.",
    };
  }

  if (normalizedShipment === "ready" && normalizedOrder === "confirmed") {
    return {
      isValid: false,
      message: "Waiting for the order to be shipped. Please try again later.",
    };
  }

  if (normalizedShipment === "ready" && normalizedOrder === "pending") {
    return {
      isValid: false,
      message: "Waiting for the order to be packed. Please try again later.",
    };
  }

  if (normalizedShipment === "shipped" && normalizedOrder === "shipped") {
    return {
      isValid: false,
      message:
        "This order has already been shipped. No further action is needed.",
    };
  }

  return { isValid: true, message: null };
};

const DeliveryStatusConfirmContent: React.FC<
  DeliveryStatusConfirmModalProps
> = ({ selectedItem, modalVisible, setModalVisible, onSuccess }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [retrievedOrder, setRetrievedOrder] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<{
    visible: boolean;
    index: number;
  }>({ visible: false, index: -1 });

  const { control, handleSubmit, reset, getValues, setValue } =
    useForm<FormData>({
      defaultValues: {
        DocNum: "",
        CustomerPortalOrderNo: "",
        OrderStatus: "",
        Warehouse: "",
        DeliveryAddress: "",
        ShipmentStatus: "",
        items: [],
      },
    });

  const { fields: itemFields } = useFieldArray({
    control,
    name: "items",
  });

  // Fetch order data
  useEffect(() => {
    const fetchOrder = async () => {
      if (!selectedItem) return;

      setLoading(true);
      setErrorMsg(null);

      try {
        const response = await getPortalOrderByOrderNo(
          selectedItem.CustomerPortalOrderNo,
        );
        const orderData = response?.data?.data || null;
        setRetrievedOrder(orderData);

        // Map items with API validation
        const mappedItems: OrderItem[] = (selectedItem.items || []).map(
          (item: any) => {
            const itemCode = item.ItemCode;

            const apiItem = orderData?.details?.find((d: any) =>
              (d.itemCodes || []).includes(itemCode),
            );

            return {
              ItemCode: item.ItemCode,
              Dscription: item.Dscription || item.itemName || "",
              RequiredDate: item.RequiredDate
                ? new Date(item.RequiredDate)
                : apiItem?.requiredDate
                  ? new Date(apiItem.requiredDate)
                  : null,
              existsInApi: !!apiItem,
            };
          },
        );
        reset({
          DocNum: selectedItem?.DocNum || "",
          CustomerPortalOrderNo: selectedItem?.CustomerPortalOrderNo || "N/A",
          OrderStatus: selectedItem?.OrderStatus || "",
          Warehouse: selectedItem?.Warehouse || "",
          DeliveryAddress: selectedItem?.DeliveryAddress || "",
          ShipmentStatus: orderData?.orderStatus || "N/A",
          items: mappedItems,
        });

        // Validate status
        const validation = getStatusValidation(
          orderData?.orderStatus,
          selectedItem?.OrderStatus,
        );
        if (!validation.isValid) {
          setErrorMsg(validation.message);
        }
      } catch (error: any) {
        console.error("Error fetching order:", error);
        setErrorMsg("Failed to fetch order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [selectedItem, reset]);

  // Handle date change
  const handleDateChange = useCallback(
    (index: number, date?: Date) => {
      if (date) {
        setValue(`items.${index}.RequiredDate`, date);
      }
      setShowDatePicker({ visible: false, index: -1 });
    },
    [setValue],
  );

  // Submit handler
  const onSubmit = async () => {
    const shipmentStatus = retrievedOrder?.orderStatus || "N/A";
    const orderStatus = selectedItem?.OrderStatus || "";

    const validation = getStatusValidation(shipmentStatus, orderStatus);

    if (!validation.isValid) {
      setErrorMsg(validation.message);
      return;
    }

    setErrorMsg(null);
    setShowConfirm(true);
  };

  // Confirm handler
  const handleConfirm = async () => {
    const values = getValues();

    // Determine shipment status based on order status
    let shipmentStatus = "Ready";
    if (selectedItem?.OrderStatus === "Shipped") {
      shipmentStatus = "Shipped";
    } else if (selectedItem?.OrderStatus === "Pending") {
      shipmentStatus = "Ready";
    }

    const payload = {
      DocNum: values.DocNum,
      CustomerPortalOrderNo: values.CustomerPortalOrderNo,
      OrderStatus: values.OrderStatus,
      Warehouse: values.Warehouse,
      DeliveryAddress: values.DeliveryAddress,
      items: values.items || [],
      PortalShipmentStatus: shipmentStatus,
    };

    setLoading(true);
    setShowConfirm(false);

    try {
      await updateDeliveryPortalStatusAddress(payload);
      setSuccessMsg("Shipment status updated successfully!");
      toast.show("Shipment status updated successfully!", { type: "success" });

      // Close modal after short delay
      setTimeout(() => {
        setModalVisible(false);
        onSuccess?.();
      }, 1000);
    } catch (error: any) {
      console.error("Error updating portal shipment:", error);
      const errorMessage = Array.isArray(error)
        ? error[0]
        : error || error?.message || "Failed to update shipment status.";
      setErrorMsg(errorMessage);
      toast.show(errorMessage, { type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    try {
      return new Date(date).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  if (loading && !retrievedOrder) {
    return (
      <View style={styles.loadingContainer}>
        <Spinner size="large" color="#24a0ed" />
        <Text style={styles.loadingText}>Loading order details...</Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Text style={styles.title}>Confirm Delivery Status</Text>

        {/* Read-only fields */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Order Information</Text>
          {[
            { name: "DocNum", label: "Document Number" },
            { name: "CustomerPortalOrderNo", label: "Portal Order No" },
            { name: "OrderStatus", label: "Order Status" },
            { name: "Warehouse", label: "Warehouse" },
            { name: "DeliveryAddress", label: "Delivery Address" },
            { name: "ShipmentStatus", label: "Shipment Status" },
          ].map(({ name, label }) => (
            <Controller
              key={name}
              control={control}
              name={name as keyof FormData}
              render={({ field: { value } }) => (
                <TextInput
                  label={label}
                  value={String(value || "")}
                  mode="outlined"
                  disabled
                  style={[styles.input]}
                  dense
                />
              )}
            />
          ))}

          {itemFields.length > 0 &&
            itemFields.map((field, index) =>
              index === 0 ? (
                <Controller
                  key={field.id}
                  control={control}
                  name={`items.${index}.RequiredDate`}
                  render={({ field: { value } }) => (
                    <TextInput
                      label="Required Date"
                      value={value ? moment(value).format("YYYY-MM-DD") : ""}
                      style={styles.input}
                      mode="outlined"
                      editable={false}
                    />
                  )}
                />
              ) : null,
            )}
        </View>

        {/* Items section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Order Items</Text>
          {itemFields.length > 0 ? (
            itemFields.map((field, index) => (
              <View key={field.id} style={styles.itemContainer}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemNumber}>Item {index + 1}</Text>
                  {!field.existsInApi && (
                    <Text style={styles.warningBadge}>
                      ⚠ Item Not Found in Portal
                    </Text>
                  )}
                </View>

                <Controller
                  control={control}
                  name={`items.${index}.ItemCode`}
                  render={({ field: { value } }) => (
                    <TextInput
                      label="Item Code"
                      value={value}
                      mode="outlined"
                      disabled
                      style={styles.input}
                      dense
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`items.${index}.Dscription`}
                  render={({ field: { value } }) => (
                    <TextInput
                      label="Description"
                      value={value}
                      mode="outlined"
                      disabled
                      style={styles.input}
                      dense
                    />
                  )}
                />
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No items available</Text>
          )}
        </View>

        {/* Messages */}
        {successMsg && (
          <View style={styles.successMessage}>
            <Text style={styles.successText}>✓ {successMsg}</Text>
          </View>
        )}
        {errorMsg && (
          <View style={styles.errorMessage}>
            <Text style={styles.errorText}>✕ {errorMsg}</Text>
          </View>
        )}

        {/* Submit button */}
        {loading ? (
          <Spinner size="small" color="#74BBFF" styles={{ marginTop: 20 }} />
        ) : (
          <Button
            mode="contained"
            onPress={handleSubmit(onSubmit)}
            style={[
              styles.submitButton,
              { backgroundColor: errorMsg ? "#ccc" : "#24a0ed" },
            ]}
            contentStyle={styles.submitButtonContent}
            disabled={!!errorMsg}
          >
            Submit
          </Button>
        )}
      </ScrollView>

      {/* Confirmation Dialog */}
      <Modal
        visible={showConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirm(false)}
      >
        <View style={[styles.confirmOverlay]}>
          <View style={styles.confirmDialog}>
            <Text style={styles.confirmTitle}>Confirm Submission</Text>
            <Text style={styles.confirmMessage}>
              Are you sure you want to confirm this delivery? This action will
              update the shipment status.
            </Text>
            <View style={styles.confirmButtons}>
              <Button
                mode="outlined"
                onPress={() => setShowConfirm(false)}
                style={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={handleConfirm}
                style={styles.confirmButton}
              >
                Yes, Confirm
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export const DeliveryStatusConfirmModal = memo(
  ({
    selectedItem,
    modalVisible,
    setModalVisible,
    onSuccess,
  }: DeliveryStatusConfirmModalProps) => {
    return (
      <Modal
        animationType="slide"
        transparent
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <BlurView intensity={90} style={styles.centeredView}>
          <View style={styles.modalView}>
            <IconButton
              icon="close"
              iconColor="#2196F3"
              size={24}
              onPress={() => setModalVisible(false)}
              style={styles.closeButton}
            />
            <DeliveryStatusConfirmContent
              selectedItem={selectedItem}
              modalVisible={modalVisible}
              setModalVisible={setModalVisible}
              onSuccess={onSuccess}
            />
          </View>
        </BlurView>
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Platform.OS === "ios" ? undefined : "rgba(0, 0, 0, 0.5)",
  },
  modalView: {
    backgroundColor: "white",
    borderRadius: 16,

    width: "95%",
    maxWidth: 600,
    maxHeight: "90%",

    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 5,
    overflow: "hidden",
  },

  scrollContent: {
    padding: 24,
    paddingTop: 16,
  },
  loadingContainer: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 12,
    color: "#666",
    fontSize: 14,
  },
  closeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 24,
    marginTop: 8,
    color: "#333",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 12,
  },
  input: {
    marginBottom: 12,
    backgroundColor: "#F5F5F5",
  },
  itemContainer: {
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#FAFAFA",
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  itemNumber: {
    fontSize: 14,
    fontWeight: "600",
    color: "#555",
  },
  warningBadge: {
    fontSize: 12,
    color: "#f57c00",
    backgroundColor: "#fff3e0",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  emptyText: {
    color: "#999",
    textAlign: "center",
    marginVertical: 20,
    fontStyle: "italic",
  },
  successMessage: {
    backgroundColor: "#e8f5e9",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#4caf50",
  },
  successText: {
    color: "#2e7d32",
    fontSize: 14,
    fontWeight: "500",
  },
  errorMessage: {
    backgroundColor: "#ffebee",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#f44336",
  },
  errorText: {
    color: "#c62828",
    fontSize: 14,
    fontWeight: "500",
  },
  submitButton: {
    marginTop: 16,
    backgroundColor: "#24a0ed",
    borderRadius: 8,
  },
  submitButtonContent: {
    paddingVertical: 8,
  },
  confirmOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  confirmDialog: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 340,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 5,
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 12,
    color: "#333",
  },
  confirmMessage: {
    fontSize: 15,
    color: "#666",
    marginBottom: 24,
    lineHeight: 22,
  },
  confirmButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  cancelButton: {
    borderColor: "#ddd",
  },
  confirmButton: {
    backgroundColor: "#24a0ed",
  },
});
